import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { EMPTY, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { DynamicDialogService } from '../../../components/dynamic-dialog/dynamic-dialog-service';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { ResponseEnumEnum } from '../../../enums/responseEnum.enum';
import { Invoice } from '../../../model/document.model';
import { DisbursementAccount } from '../../estimage-pay-page/estimage-pay-invoices/estimage-pay-invoices.model';
import { FactoringCompany } from '../../../model/factoring-company.model';
import { ToolbarService } from '../../../services/toolbar.service';
import { UserService } from '../../../services/user.service';

export interface InvoiceState extends EntityState<Invoice> {
  isLoading: boolean;
  factoringCompanies: FactoringCompany[];
}

export const adapter: EntityAdapter<Invoice> = createEntityAdapter<Invoice>();
export const initialState: InvoiceState = adapter.getInitialState({
  isLoading: false,
  factoringCompanies: []
});

const { selectAll } = adapter.getSelectors();

@Injectable({ providedIn: 'root' })
export class InvoiceStore extends ComponentStore<InvoiceState> {
  invoice = this.selectSignal((state) => selectAll(state));
  isLoading = this.selectSignal((state) => state.isLoading);
  factoringCompanies = this.selectSignal((state) => state.factoringCompanies);

  #dynamicDialogService = inject(DynamicDialogService);
  #message = inject(MessagingService);
  #toolbarService = inject(ToolbarService);
  #userService = inject(UserService);
  refreshInvoice: WritableSignal<any> = signal(false);

  constructor() {
    super(initialState);
  }

  openDynamicDialog = this.effect((trigger$: Observable<MatDialogConfig>) =>
    trigger$.pipe(
      switchMap((dialogConfig) => {
        return this.#dynamicDialogService
          .openDialog(dialogConfig)
          .afterClosed()
          .pipe(
            tapResponse({
              next: (response: ResponseEnumEnum) => {
                this.#dynamicDialogService.dialogRefSignal.set(null);
                response === ResponseEnumEnum.SUCCESS && this.refreshInvoice.set(true);
              },
              error: () => {
                return EMPTY;
              }
            })
          );
      })
    )
  );

  getDisbursementAccounts = this.effect((trigger$: Observable<MatDialogConfig>) =>
    trigger$.pipe(
      tap(() => {
        this.#toolbarService.setCloudState('FETCHING');
      }),
      switchMap((dialogConfig) => {
        return this.#userService.getDisbursementAccounts().pipe(
          tapResponse({
            next: (disbursementAccounts: DisbursementAccount[]) => {
              if (disbursementAccounts && dialogConfig) {
                dialogConfig.data.estImagePayRequest.disbursementAccounts = disbursementAccounts;
                this.openDynamicDialog(dialogConfig);
              }
              this.#toolbarService.setCloudState('RESTING');
            },
            error: () => {
              this.openDynamicDialog(dialogConfig);
              this.#toolbarService.setCloudState('RESTING');
            }
          })
        );
      })
    )
  );

  useEstimagePay = this.effect((trigger$: Observable<{ invoiceId: number; accountId: string }>) =>
    trigger$.pipe(
      tap(() => {
        this.#toolbarService.setCloudState('FETCHING');
      }),
      switchMap((data) => {
        const { invoiceId, accountId } = data;
        return this.#userService.useEstimagePayForInvoice(invoiceId, accountId).pipe(
          tapResponse({
            next: () => {
              this.#dynamicDialogService.closeDialog(
                ResponseEnumEnum.SUCCESS,
                'Disbursement Account selected for EstImage Pay'
              );
              this.#toolbarService.setCloudState('RESTING');
            },
            error: () => {
              this.#dynamicDialogService.closeDialog(
                ResponseEnumEnum.ERROR,
                'Could not select Disbursement Account for EstImage Pay'
              );
              this.#toolbarService.setCloudState('RESTING');
            }
          })
        );
      })
    )
  );

  removeEstimagePay = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      tap(() => this.#toolbarService.setCloudState('FETCHING')),
      switchMap((invoiceId) => {
        return this.#userService.removeEstimagePayForInvoice(invoiceId).pipe(
          tapResponse({
            next: () => this.refreshInvoice.set(true),
            error: () => this.#message.error('Could not remove Disbursement Account for EstImage Pay'),
            complete: () => this.#toolbarService.setCloudState('RESTING')
          })
        );
      })
    )
  );

  getFactoringCompanies = this.effect((trigger$) =>
    trigger$.pipe(
      tap(() => this.#toolbarService.setCloudState('FETCHING')),
      switchMap(() =>
        this.#userService.getFactoringCompaniesByCountry().pipe(
          tapResponse({
            next: (companies) => {
              const sortedCompanies = companies.sort((a, b) => a.companyName.localeCompare(b.companyName));
              this.patchState({ factoringCompanies: sortedCompanies });
            },
            error: (error) => this.#message.error('Could not retrieve the list of factoring companies.')
          })
        )
      )
    )
  );
}
